import { Alert, Paper, Select, Stack, Text, Title } from '@mantine/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useTranslation } from '@refinedev/core';
import { useEffect, useMemo, useState } from 'react';

import { useListProvidersBySite } from 'api/resource';
import { useCurrentSiteStore } from 'store/SiteStore';
import { toTitleCase } from 'utils/stringUtils';

import { ProviderDashboard } from './ProviderDashboard';

export const AdminProviderDashboard = () => {
  useDocumentTitle('Provider Dashboard Preview | QBilling');
  const { translate } = useTranslation();
  const { id: siteID } = useCurrentSiteStore();
  const {
    data: providers = [],
    isLoading,
    isError,
  } = useListProvidersBySite({ siteID });
  const [selectedProviderID, setSelectedProviderID] = useState<string | null>(
    null
  );

  useEffect(() => {
    setSelectedProviderID(null);
  }, [siteID]);

  const portalProviders = useMemo(
    () => providers.filter((provider) => provider.userID || provider.user),
    [providers]
  );
  const selectedProvider = portalProviders.find(
    (provider) => provider.id === selectedProviderID
  );
  const selectedUserID = selectedProvider?.userID ?? selectedProvider?.user?.id;
  const providerOptions = portalProviders.map((provider) => ({
    value: provider.id,
    label: `${provider.title || 'Dr.'} ${toTitleCase(
      provider.firstName,
      provider.lastName
    )}`,
  }));

  return (
    <Stack gap="md" data-testid="admin-provider-dashboard-page">
      <Paper p="md" radius="md">
        <Stack gap="xs">
          <Title order={2}>
            {translate(
              'pages:providerDashboard.admin.title',
              'View Provider Dashboard'
            )}
          </Title>
          <Text c="dimmed" size="sm">
            {translate(
              'pages:providerDashboard.admin.description',
              'Select a provider with portal access to preview their dashboard.'
            )}
          </Text>
          <Select
            label={translate(
              'pages:providerDashboard.admin.providerLabel',
              'Provider'
            )}
            placeholder={translate(
              'pages:providerDashboard.admin.providerPlaceholder',
              'Select a provider'
            )}
            data={providerOptions}
            value={selectedProviderID}
            onChange={setSelectedProviderID}
            searchable
            clearable
            disabled={!siteID || isLoading}
            nothingFoundMessage={translate(
              'pages:providerDashboard.admin.noProviders',
              'No providers with portal access found'
            )}
            data-testid="admin-provider-select"
          />
          {isError && (
            <Alert color="red">
              {translate(
                'pages:providerDashboard.admin.loadError',
                'Providers could not be loaded.'
              )}
            </Alert>
          )}
        </Stack>
      </Paper>

      {selectedProvider && selectedUserID ? (
        <ProviderDashboard
          provider={selectedProvider}
          userID={selectedUserID}
        />
      ) : (
        !isLoading &&
        !isError && (
          <Alert color="blue">
            {portalProviders.length
              ? translate(
                  'pages:providerDashboard.admin.selectPrompt',
                  'Select a provider to view their dashboard.'
                )
              : translate(
                  'pages:providerDashboard.admin.empty',
                  'This site has no providers with portal access.'
                )}
          </Alert>
        )
      )}
    </Stack>
  );
};
